
import { fetchTextWithTimeout } from '@scale/utilities';

export function calculateDiscount(total, type, value) {
  const v = Number(value) || 0;
  switch (type) {
    case 'percentage': return Math.max(0, Math.round(total * (1 - v / 100)));
    case 'fixed_amount': return Math.max(0, total - Math.round(v * 100));
    default: return total;
  }
}

export function calculateBogo(unitPrice, buyQty, getQty, getDiscountPct) {
  return unitPrice * buyQty + Math.round(unitPrice * getQty * (1 - getDiscountPct / 100));
}

export function calculateBogoFromPrices(prices, getQty, getDiscountPct) {
  const validPrices = prices.map(Number).filter((price) => Number.isFinite(price) && price >= 0);
  const giftCount = Math.max(0, Math.min(validPrices.length, Number(getQty) || 0));
  const percentage = Math.max(0, Math.min(100, Number(getDiscountPct) || 0));
  const discount = [...validPrices]
    .sort((left, right) => left - right)
    .slice(0, giftCount)
    .reduce((total, price) => total + (price * percentage / 100), 0);
  return validPrices.reduce((total, price) => total + price, 0) - discount;
}

export function buildBundleProperties({ type, sectionId, name, offerId, bundleId, proof, proofDay }) {
  if (offerId && (!bundleId || !proof || !proofDay)) throw new Error(Scale.translations.bundleUnverified);
  const props = {
    _bundle_id: bundleId ?? `${type}-${sectionId}-${crypto.randomUUID()}`,
    _bundle_type: type,
  };
  if (name) props._bundle_name = name;
  if (offerId) {
    props._bundle_offer_id = offerId;
    props._bundle_proof = proof;
    props._bundle_proof_day = proofDay;
  }
  return props;
}

const bundleRuleSyncs = new Map();

export function syncBundleRule({ offerId, bundleId, templateKey, currentProductHandle }) {
  if (!offerId) return Promise.resolve();
  const cacheKey = `${offerId}:${bundleId}`;
  if (bundleRuleSyncs.has(cacheKey)) return bundleRuleSyncs.get(cacheKey);

  const sync = (async () => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const { response, text } = await fetchTextWithTimeout(`${root}apps/scale-ova/proxy/bundle-discount/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ offerId, bundleId, templateKey, currentProductHandle }),
        timeout: 10000,
      }).catch((error) => {
        const message = error?.name === 'TimeoutError' ? Scale.translations.bundleTimeout : Scale.translations.bundleUnverified;
        throw new Error(message, { cause: error });
      });
      let payload = null;
      try { payload = JSON.parse(text); } catch {   }
      if (!response.ok || payload?.success !== true) {
        throw new Error(Scale.translations.bundleUnverified, { cause: payload?.error });
      }
      return payload;
    } finally {
      bundleRuleSyncs.delete(cacheKey);
    }
  })();

  bundleRuleSyncs.set(cacheKey, sync);
  return sync;
}

export function applyCartContributions(items, contributions) {
  const active = contributions.filter((contribution) => contribution?.items?.length);
  const replacements = active.filter((contribution) => contribution.replace);
  if (replacements.length > 1) {
    throw new Error(Scale.translations.bundleConflict);
  }

  const baseProps = items[0]?.properties;
  const baseSellingPlan = items[0]?.selling_plan;
  let result = replacements.length === 1
    ? replacements[0].items.map((item) => ({
        ...item,
        properties: baseProps ? { ...baseProps, ...(item.properties ?? {}) } : item.properties,
        selling_plan: item.selling_plan ?? baseSellingPlan,
      }))
    : [...items];

  for (const contribution of active) {
    if (!contribution.replace) result.push(...contribution.items);
  }
  return mergeItemsById(result);
}

function mergeItemsById(items) {
  const byKey = new Map();
  for (const item of items) {
    const key = `${item.id}-${item.selling_plan ?? ''}-${JSON.stringify(item.properties ?? {})}`;
    const existing = byKey.get(key);
    if (existing) existing.quantity += item.quantity;
    else byKey.set(key, { ...item });
  }
  return [...byKey.values()];
}
